import { useRef, useState } from "react";
import { base44 } from "@/api/base44Client";
import { Image } from "@/components/ui/image";
import { Upload, Link2, Loader2, X } from "lucide-react";

export default function ImageUpload({ value, onChange, multiple = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [showUrl, setShowUrl] = useState(false);
  const [urlText, setUrlText] = useState("");

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setUploading(true);
    try {
      // Every photo goes up at once, so a gallery is one upload wide rather
      // than the sum of its pictures — and each is stored exactly as it came
      // off the camera. Nothing waits on a second pass: the site resizes and
      // re-encodes a photo for whatever spot it lands in when it is served.
      setStatus(arr.length > 1 ? `Uploading ${arr.length} photos…` : "Uploading photo…");
      const urls = await Promise.all(
        arr.map((file) =>
          base44.integrations.Core.UploadFile({ file }).then(({ file_url }) => file_url)
        )
      );
      if (multiple) onChange([...(value || []), ...urls]);
      else onChange(urls[0]);
    } catch (e) {
      alert(e.message || "Upload failed");
    } finally {
      setUploading(false);
      setStatus("");
    }
  };

  // A linked photo is taken as it is, the same way an uploaded one is.
  const addUrl = () => {
    const url = urlText.trim();
    if (!url) return;
    setUrlText("");
    setShowUrl(false);
    if (multiple) onChange([...(value || []), url]);
    else onChange(url);
  };

  const removeAt = (i) => {
    if (multiple) onChange((value || []).filter((_, idx) => idx !== i));
    else onChange("");
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
        }}
        className="cursor-pointer rounded-[12px] border-2 border-dashed border-[#cfcfca] bg-[#fafaf7] hover:border-[#171717] px-4 py-6 grid place-items-center text-center transition-colors"
      >
        {uploading ? (
          <div className="flex items-center gap-2 text-[13px] font-bold text-[#6d6d6a]">
            <Loader2 className="w-4 h-4 animate-spin" /> {status || "Working…"}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#6d6d6a]">
            <Upload className="w-5 h-5" />
            <span className="text-[13px] font-bold text-[#171717]">
              Click or drop image{multiple ? "s" : ""} here
            </span>
            <span className="text-[11px]">Uploaded as-is — no processing wait</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          className="hidden"
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {multiple && (value || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {(value || []).map((url, i) => (
            <div key={i} className="relative w-16 h-16 rounded-[8px] overflow-hidden border border-[#deded9]">
              <Image src={url} alt="" fittingType="fill" className="w-full h-full" />
              <button
                onClick={() => removeAt(i)}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 grid place-items-center"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!multiple && value && (
        <div className="relative w-full h-28 rounded-[10px] overflow-hidden border border-[#deded9] mt-2">
          <Image src={value} alt="" fittingType="fill" className="w-full h-full" />
          <button
            onClick={() => removeAt(0)}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 grid place-items-center"
            aria-label="Remove"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="mt-2">
        {!showUrl ? (
          <button
            onClick={() => setShowUrl(true)}
            className="text-[12px] font-bold text-[#6d6d6a] underline inline-flex items-center gap-1"
          >
            <Link2 className="w-3.5 h-3.5" /> Paste URL instead
          </button>
        ) : (
          <div className="flex gap-2">
            <input
              value={urlText}
              onChange={(e) => setUrlText(e.target.value)}
              placeholder="https://image-url.jpg"
              className="flex-1 rounded-[10px] border border-[#deded9] px-3 py-2 text-[13px] outline-none focus:border-[#171717]"
            />
            <button onClick={addUrl} className="rounded-full bg-[#171717] text-white px-3 text-[12px] font-bold">
              Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
}